import * as Cookies from 'js-cookie';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import type * as addressActions from '~/actions/address.actions';
import type * as carrierActions from '~/actions/carrier-accounts.actions';
import type * as ordersActions from '~/actions/orders.actions';

import { ActionKeys } from '~/actions/constants';
import * as userActions from '~/actions/user.actions';
import {
  STORE_CONNECTION_SUCCESS_COOKIE_NAME,
  USER_REGISTER_SUCCESS_COOKIE_NAME,
} from '~/constants/cookies';
import {
  BATCH_PURCHASED_EVENT,
  ORDERS_COUNT,
  STORE_CONNECTION_SUCCESS_EVENT,
  USER_REGISTER_EVENT,
} from '~/constants/gtm';
import { CARRIER_REGISTRATION_SUCCESS } from '~/constants/user-events';
import { isNewOnboardingVariation } from '~/modules/onboarding/utils/onboarding.util';
import { getRatesForBatchEntriesToBuy } from '~/selectors/batch-entry.composite.selectors';
import { getBatchEntryData } from '~/selectors/batch-entry.selectors';
import { getPaymentMethods } from '~/selectors/billing.selectors';
import { getLabelSettings } from '~/selectors/label-settings.selectors';
import {
  getAllOrdersCount,
  getIsLoading,
  getOrdersCount,
  getOrdersData,
  getSelectedOrders,
} from '~/selectors/orders.selectors';
import {
  getActivationData,
  getDefaultParcel,
  getUserAddress,
  getUserDataForFeatureTests,
  getUserId,
  getUserReturnAddress,
} from '~/selectors/user.selectors';
import { sendGTMEvent } from '~/services/gtm.service';
import { getCurrentUser } from '~/services/user.service';
import { trackingHelper } from '~/utils/TrackingHelper';
import {
  getBatchEntryIds,
  getTrackedOrderDetailsByShipmentFromOrders,
} from '~/utils/TrackingHelper/trackedOrderDetails';
import { ADDRESS_TRACKING_EVENT, ORDER_TRACKING_EVENT } from '~/utils/TrackingHelper/types';
import { trackAddressUpdate, trackOrderUpdate } from '~/utils/TrackingHelper/utils';
import { FeatureFlags, decideFeatureFlag } from '~/utils/featureflags';
import { type FeatureFlagUserData } from '~/utils/featureflags/types';
import { FullStoryEvent, fullStoryTracking } from '~/utils/fullstory/tracking';
import { Events, identify, track } from '~/utils/segment';
import { extractMarketingPayloadFromCookie } from '~/utils/utm-tracking';

import { selectState } from '..';

export const registerSuccessCookie = () =>
  Cookies.get(USER_REGISTER_SUCCESS_COOKIE_NAME) || 'false';

export function* sendRegisterSuccessEvent() {
  const registerSuccessCookieValue: string = yield call(registerSuccessCookie);

  if (registerSuccessCookieValue === 'true') {
    yield call(sendGTMEvent, {
      eventName: USER_REGISTER_EVENT,
    });

    const userResponse = yield call(getCurrentUser);
    const UTMCookies = Cookies.getJSON('shippoUTM');
    const marketingPayload = UTMCookies ? extractMarketingPayloadFromCookie(UTMCookies) : undefined;
    const storeReferral = localStorage.getItem('registration_store') ?? undefined;

    const authType = userResponse?.data.federated_emails?.length > 0 ? 'sso' : 'email';
    const onboardingFlag = decideFeatureFlag(FeatureFlags.ONBOARDING_2026, undefined);

    yield call(fullStoryTracking, FullStoryEvent.SIGNED_UP, {
      is_onboarding_2026: isNewOnboardingVariation(onboardingFlag?.variation),
      onboarding_2026_variation: onboardingFlag?.variation || 'off',
      type: authType,
    });

    yield call(track, Events.registered, {
      email: userResponse?.data.email,
      first_name: userResponse?.data.first_name,
      last_name: userResponse?.data.last_name,
      registration_ad_param1: marketingPayload?.shAd1,
      registration_ad_param2: marketingPayload?.shAd2,
      registration_ad_param3: marketingPayload?.shAd3,
      registration_ad_param4: marketingPayload?.shAd4,
      registration_campaign: marketingPayload?.campaign,
      registration_click_identifier: marketingPayload?.attributionSnippet,
      registration_date: userResponse?.data.date_joined,
      registration_medium: marketingPayload?.medium,
      registration_method: marketingPayload?.regMethod,
      registration_source: marketingPayload?.source,
      store_referral: storeReferral,
      userId: userResponse?.data.id.toString(),
      userId_integer: userResponse?.data.id,
    });

    yield call(Cookies.remove, USER_REGISTER_SUCCESS_COOKIE_NAME, {
      domain: CONFIG.shippoRootUrl,
    });
  }
}

export function* getStoreConnectionSuccessCookie() {
  yield Cookies.get(STORE_CONNECTION_SUCCESS_COOKIE_NAME) || '';
}

export function* sendStoreConnectionSuccessEvent() {
  const storeConnectionSuccessCookieValue: string = yield call(getStoreConnectionSuccessCookie);
  if (storeConnectionSuccessCookieValue) {
    yield call(sendGTMEvent, {
      data: {
        storeName: storeConnectionSuccessCookieValue,
      },
      eventName: STORE_CONNECTION_SUCCESS_EVENT,
    });
    // @NOTE: Track events will be added back at a later date
    yield call(track, Events.store_connected, {
      store_name: storeConnectionSuccessCookieValue,
    });
    yield call(Cookies.remove, STORE_CONNECTION_SUCCESS_COOKIE_NAME, {
      domain: CONFIG.shippoRootUrl,
    });
  }
}

function* sendOrderCount() {
  const ordersCount = yield select(getOrdersCount);
  const userOrderCount = yield select(getAllOrdersCount);
  const activationData = yield select(getActivationData);
  const isLoading = yield select(getIsLoading);
  const userId = yield select(getUserId);

  if (!isLoading && ordersCount !== activationData.ordersCount) {
    yield call(sendGTMEvent, {
      data: {
        ordersCount,
      },
      eventName: ORDERS_COUNT,
    });
    yield put(userActions.updateActivationData({ ordersCount }));
    yield call(identify, userId, { orders_count: userOrderCount });
  }
}

function* sendBatchPurchasedEvent() {
  const getUserData: FeatureFlagUserData | null = yield selectState<FeatureFlagUserData | null>(
    getUserDataForFeatureTests,
  );

  // first label purchased GTM and Heap
  if (getUserData && !getUserData.hasPurchasedLabel) {
    yield put(userActions.updateFirstLabelPurchased());
    yield call([localStorage, localStorage.setItem], 'has_purchased_first_label', 'true');
  }
  yield call(sendGTMEvent, {
    eventName: BATCH_PURCHASED_EVENT,
  });
}

/**
 * User has saved an address, send event to GTM
 * @param action
 */
function* sendAddressEvent(action: ReturnType<typeof addressActions.createAPIAddressSuccess>) {
  yield call(track, Events.address_created, {
    is_default_return: action.payload.address.isDefaultReturn,
    is_default_sender: action.payload.address.isDefaultSender,
  });

  trackAddressUpdate(action.payload.trackingReference, ADDRESS_TRACKING_EVENT.operations.CREATED, {
    addressId: action.payload.address.id,
  });
}

/**
 * User registered a carrier account, send event to GTM
 * @param action
 */
function* sendCreateCarrierAccountEvent(
  action: ReturnType<typeof carrierActions.createCarrierAccountSuccess>,
) {
  const carrierAccount = action.payload.carrierAccount;
  yield call(sendGTMEvent, {
    data: {
      carrierName: carrierAccount.carrier.name,
    },
    eventName: CARRIER_REGISTRATION_SUCCESS,
  });
}

/**
 * User connected a carrier account, send event to GTM
 * @param action
 */
function* sendConnectCarrierAccountEvent(
  action: ReturnType<typeof carrierActions.connectCarrierAccountSuccess>,
) {
  yield call(track, Events.carrier_connected, {
    carrier_name: action.payload.carrierAccount.carrier.name,
  });
}

export function* trackOrderSelection(
  action:
    | ReturnType<typeof ordersActions.clearSelectedOrders>
    | ReturnType<typeof ordersActions.selectOrder>,
) {
  if (!action.trackEvent) return;

  const selectedOrders = yield select(getSelectedOrders);

  const trackingReference = trackingHelper.amendTrackingReferenceOrderSelection(
    trackingHelper.getTrackingReference(ORDER_TRACKING_EVENT.name, 'order_table', {}),
    selectedOrders,
  );

  if (trackingReference.orderQuantity === undefined) {
    if (action.type === ActionKeys.ORDERS_SELECT_ORDER) {
      // in classic orders page individual checkbox deselection, we can figure out the previously selected order
      // from the action payload...
      yield call(
        trackOrderUpdate,
        trackingHelper.amendTrackingReferenceOrders(trackingReference, Object.keys(action.payload)),
        ORDER_TRACKING_EVENT.operations.DESELECTED,
        {},
      );
    }

    // ...but deselecting all does not include the payload of previously selected orders
    yield call(trackOrderUpdate, trackingReference, ORDER_TRACKING_EVENT.operations.DESELECTED, {});
  } else {
    const batchEntryIds = getBatchEntryIds(selectedOrders);
    const ordersData = yield select(getOrdersData);
    const batchEntries = yield select(getBatchEntryData);
    const ratesForOrders = yield select(getRatesForBatchEntriesToBuy, { batchEntryIds });
    const defaultSenderAddress = yield select(getUserAddress);
    const defaultReturnAddress = yield select(getUserReturnAddress);
    const defaultParcelTemplate = yield select(getDefaultParcel);
    const paymentMethods = yield select(getPaymentMethods);
    const labelSettings = yield select(getLabelSettings);
    const { paymentType, stripeToken } = paymentMethods || {};

    const trackedOrderDetailsByShipment = getTrackedOrderDetailsByShipmentFromOrders({
      batchEntries,
      defaultParcelTemplate,
      defaultReturnAddress,
      defaultSenderAddress,
      insuranceSettings: trackingReference.userDetails?.defaultInsuranceSetings,
      labelSettings,
      ordersData,
      paymentType,
      ratesForOrders,
      selectedOrders,
      stripeToken,
    });

    const trackingReferenceWithOrderDetails =
      trackingHelper.amendTrackingReferenceTrackedOrderDetailsByShipment(
        trackingReference,
        trackedOrderDetailsByShipment,
      );

    yield call(
      trackOrderUpdate,
      trackingReferenceWithOrderDetails,
      ORDER_TRACKING_EVENT.operations.VIEWED,
      {},
    );
  }
}

export default [
  takeLatest(ActionKeys.USER_DID_AUTHENTICATE, sendRegisterSuccessEvent),
  takeLatest(ActionKeys.USER_DID_AUTHENTICATE, sendStoreConnectionSuccessEvent),
  takeLatest(ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_SUCCESS, sendBatchPurchasedEvent),
  takeLatest(ActionKeys.APIADDRESS_CREATE_SUCCESS, sendAddressEvent),
  takeLatest(ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS, sendCreateCarrierAccountEvent),
  takeLatest(ActionKeys.CONNECT_CARRIER_ACCOUNT_SUCCESS, sendConnectCarrierAccountEvent),
  takeLatest([ActionKeys.ORDERS_FETCH_SUCCESS], sendOrderCount),
  takeLatest(
    [ActionKeys.ORDERS_SELECT_ORDER, ActionKeys.ORDERS_CLEAR_SELECTED],
    trackOrderSelection,
  ),
];
